





















































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { AxiosResponse } from 'axios';

// Interfaces for structured dynamic variables, theme settings, and API response
interface DynamicVariables {
  entity_id?: number;
  entity_name?: string;
  entity_key?: string;
  call_id?: number;
  call_public_id?: string;
  flow_id?: number;
  flow_public_id?: string;
  [key: string]: unknown;
}

interface ThemeSettings {
  header_bg_color?: string;
  header_text_color?: string;
  footer_bg_color?: string;
  footer_text_color?: string;
}

interface SessionResponse {
  success: boolean;
  signed_url?: string;
  dynamic_variables?: DynamicVariables;
  theme?: ThemeSettings;
  error?: string;
}

@Component
export default class WidgetView extends Vue {
  @Prop({ type: String, required: true }) readonly entity_key!: string;
  @Prop({ type: String, required: true }) readonly agent_id!: string;

  // Optional props for default fallback colors
  @Prop({ type: String, default: '#1e293b' }) readonly headerBgColor!: string;
  @Prop({ type: String, default: '#ffffff' }) readonly headerTextColor!: string;
  @Prop({ type: String, default: '#f8fafc' }) readonly footerBgColor!: string;
  @Prop({ type: String, default: '#64748b' }) readonly footerTextColor!: string;

  private readonly API_HOST = process.env.VUE_APP_ROOT_API || '/widget-api';

  loading = false;
  errorMessage: string | null = null;
  isWidgetActive = false;
  entityName = '';

  // Reactive properties for API dynamic colors
  apiHeaderBg: string | null = null;
  apiHeaderTextColor: string | null = null;
  apiFooterBg: string | null = null;
  apiFooterTextColor: string | null = null;

  /**
   * Computed properties prioritizing server colors over default props
   */
  get activeHeaderBg(): string {
    return this.apiHeaderBg || this.headerBgColor;
  }

  get activeHeaderTextColor(): string {
    return this.apiHeaderTextColor || this.headerTextColor;
  }

  get activeFooterBg(): string {
    return this.apiFooterBg || this.footerBgColor;
  }

  get activeFooterTextColor(): string {
    return this.apiFooterTextColor || this.footerTextColor;
  }

  /**
   * Computed property to extract the current year dynamically
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  async mounted() {
    this.injectWidgetScript();
    await this.startSession();
  }

  /**
   * Injects the ElevenLabs web component script into the document head
   */
  private injectWidgetScript(): void {
    const scriptId = 'elevenlabs-convai-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
      script.async = true;
      document.head.appendChild(script);
    }
  }

  /**
   * Initiates the AI assistant session by requesting session tokens and theme config from the server
   */
  async startSession(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const formData = new FormData();
      formData.append('entity_key', this.entity_key);
      formData.append('agent_id', this.agent_id);

      const response: AxiosResponse<SessionResponse> = await this.axios.post(
        `${this.API_HOST}/callcenter/ai/widget/session`,
        formData
      );

      const data = response.data;

      if (!data.success || !data.signed_url) {
        this.errorMessage = data.error || 'Unspecified server error.';
        return;
      }

      // Extract entity_name from dynamic_variables
      if (data.dynamic_variables?.entity_name) {
        this.entityName = data.dynamic_variables.entity_name;
      }

      // Extract theme colors from API response if present
      if (data.theme) {
        this.apiHeaderBg = data.theme.header_bg_color || null;
        this.apiHeaderTextColor = data.theme.header_text_color || null;
        this.apiFooterBg = data.theme.footer_bg_color || null;
        this.apiFooterTextColor = data.theme.footer_text_color || null;
      }

      this.mountWidget(data.signed_url, data.dynamic_variables);
      this.isWidgetActive = true;

    } catch (error) {
      console.error('Failed to request session:', error);
      this.errorMessage = 'Could not connect to the application server.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Mounts the custom web component and injects Shadow DOM style overrides
   */
  private mountWidget(signedUrl: string, dynamicVariables?: DynamicVariables): void {
    const container = this.$refs.widgetContainer as HTMLElement;
    if (!container) return;

    container.innerHTML = '';

    const widget = document.createElement('elevenlabs-convai');
    widget.setAttribute('signed-url', signedUrl);
    
    if (dynamicVariables) {
      widget.setAttribute('dynamic-variables', JSON.stringify(dynamicVariables));
    }
    
    widget.setAttribute('variant', 'expanded');
    
    // Disconnection listeners
    widget.addEventListener('elevenlabs-convai:call-end', () => {
      this.handleDisconnect('The assistant has ended the conversation.');
    });

    widget.addEventListener('elevenlabs-convai:disconnect', () => {
      this.handleDisconnect('Connection lost or session timed out.');
    });

    container.appendChild(widget);

    // Direct style injection targeting internal shadow root classes .sheet and .overlay
    setTimeout(() => {
      if (widget.shadowRoot) {
        const style = document.createElement('style');
        style.textContent = `
          .overlay {
            position: static !important;
            height: 100% !important;
          }

          .sheet, [data-variant="compact"].sheet {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            top: 0 !important;
            left: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
        `;
        widget.shadowRoot.appendChild(style);
      }
    }, 100);
  }

  /**
   * Resets component state upon session disconnection
   */
  private handleDisconnect(reasonMessage: string): void {
    this.isWidgetActive = false;
    this.errorMessage = reasonMessage;
    const container = this.$refs.widgetContainer as HTMLElement;
    if (container) {
      container.innerHTML = '';
    }
  }
}
